/**
 * Annual support yearly digest tick — pure helper extracted from
 * coreSlice.nextDay (02/06/2026 night refactor).
 *
 * At fiscal year close (alongside the annual report), every hired support
 * character pushes a personalised year-end message grounded in actual
 * KPIs (CA, contrats, churn, pannes, IS, reputation deltas).
 *
 * This helper does the data-gathering plumbing : it reads from the live
 * store state, computes reputation/notoriety deltas, fired/attrition
 * counts, and fiscal extras. The actual message-building lives in
 * `supportYearlyDigest.buildSupportYearlyDigest` ; this helper just
 * returns the array of messages.
 *
 * Pure (apart from reading live state from the get() snapshot passed
 * in by the caller). No `set()`.
 */

import { buildSupportYearlyDigest } from './supportYearlyDigest';

/**
 * @param {Object} liveState - the live store state (get())
 * @param {Object} annualReport - the freshly-built annual report
 * @param {number} newYear - next-tick year (used to bound the closing year)
 * @returns {Array<MessagePayload>} — empty array if nothing to push
 */
export function buildSupportYearlyDigestMessages(liveState, annualReport, newYear) {
  try {
    // Compute reputation / notoriety deltas from repHistory.
    const repHistory = liveState.repHistory || [];
    const startEntry =
      repHistory.find(h => h.year === annualReport.year && h.month === 1)
      || repHistory[0]
      || { reputation: 50, notoriety: 0 };
    const reputationDelta = Math.round((annualReport.reputation || 0) - startEntry.reputation);
    const notorietyDelta = Math.round((annualReport.notoriety || 0) - (startEntry.notoriety || 0));

    // Year-bound churn counts.
    const yearStartDay = (newYear - 2) * 360; // start of the closing year
    const yearEndDay = (newYear - 1) * 360;
    const firedDriversCount = Object.values(liveState.firedDriversArchive || {})
      .filter(a => a && typeof a.firedAt === 'number' && a.firedAt >= yearStartDay && a.firedAt < yearEndDay)
      .length;
    const attritionThisYear = (liveState.attritionHistory || [])
      .filter(h => h && typeof h.day === 'number' && h.day >= yearStartDay && h.day < yearEndDay)
      .length;

    // Fiscal extras.
    const totalIsPaid = (liveState.isInstallmentsPaid || []).reduce((s, x) => s + (x.amount || 0), 0);
    const isOptimizationsActive = Object.keys(liveState.activeOptimizations || {}).length;

    const messages = buildSupportYearlyDigest(liveState, annualReport, {
      reputationDelta,
      notorietyDelta,
      breakdownsThisYear: liveState.breakdownsThisYear || 0,
      firedDriversCount,
      attritionThisYear,
      totalIsPaid,
      isOptimizationsActive,
      // 17/06/2026 (Lot 4) — new annual KPIs feeding enriched digests :
      transfersInitiatedThisYear: liveState.transfersInitiatedThisYear || 0,
      holidaysGrantedThisYear: liveState.holidaysGrantedThisYear || 0,
      holidayDriversWentOnLeaveThisYear: liveState.holidayDriversWentOnLeaveThisYear || 0,
      vehiclesScrappedThisYear: liveState.vehiclesScrappedThisYear || 0,
      fuelTheftsResolvedThisYear: liveState.fuelTheftsResolvedThisYear || 0,
    });
    return Array.isArray(messages) ? messages : [];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[supportYearlyDigestTick] failed:', err);
    return [];
  }
}
